import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { deepDivePages } from '../../content/deepDivePages';
import './About.css';

const About = () => {
  return (
    <div className="about-section">
      <div className="about-container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="section-badge">
            <span className="badge-icon">🌊</span>
            OUR APPROACH
          </div>
          <h2 className="section-title">
            Engineering Digital Systems<br />
            That Support How You Work
          </h2>
        </motion.div>

        <motion.div
          className="about-content-grid"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="about-images">
            <div className="dragon-showcase">
              <div className="dragon-variant">
                <div className="dragon-img-container">
                  <picture>
                    <source srcSet="/images/Banner-Image-1.webp" type="image/webp" />
                    <img
                      src="/images/Banner-Image-1.png"
                      alt="Leviathan Coding Systems - Professional Web Development Studio"
                      loading="lazy"
                      decoding="async"
                      width="1536"
                      height="1024"
                    />
                  </picture>
                </div>
              </div>
              
              <div className="dragon-variant">
                <div className="dragon-img-container">
                  <picture>
                    <source srcSet="/images/Banner-image-2.webp" type="image/webp" />
                    <img
                      src="/images/Banner-image-2.png"
                      alt="Bespoke Digital Systems and Web Solutions for North West Businesses"
                      loading="lazy"
                      decoding="async"
                      width="1536"
                      height="1024"
                    />
                  </picture>
                </div>
              </div>
              
              <div className="dragon-variant">
                <div className="dragon-img-container">
                  <picture>
                    <source srcSet="/images/Banner-image-3.webp" type="image/webp" />
                    <img
                      src="/images/Banner-image-3.png"
                      alt="Custom Web Development and Digital Infrastructure Solutions"
                      loading="lazy"
                      decoding="async"
                      width="1536"
                      height="1024"
                    />
                  </picture>
                </div>
              </div>
            </div>
          </div>

          <div className="about-text">
            <p>
              Leviathan Coding Systems was founded with a systems-first mindset.
            </p>
            <p>
              Rather than forcing businesses into rigid platforms, we design and build digital 
              tools around how teams actually operate day-to-day.
            </p>
            <p>
              With experience in structured, operational environments, our approach to web 
              development prioritises reliability, clarity, and scalability. Every project is 
              treated as a long-term system <span className="gradient-text">not a quick build</span> ensuring it remains secure, 
              adaptable, and easy to evolve as your business grows.
            </p>
            <p>
              We work closely with clients, translating real-world problems into clean, 
              maintainable technology that delivers practical, measurable value.
            </p>

            <div className="principles-grid">
              <div className="principle-item">
                <div className="principle-icon">✓</div>
                <div className="principle-content">
                  <h4>Honest scope</h4>
                  <p>Clear boundaries and realistic outcomes</p>
                </div>
              </div>
              <div className="principle-item">
                <div className="principle-icon">📈</div>
                <div className="principle-content">
                  <h4>Built to scale</h4>
                  <p>Solid foundations with room to grow</p>
                </div>
              </div>
              <div className="principle-item">
                <div className="principle-icon">🇬🇧</div>
                <div className="principle-content">
                  <h4>UK-focused</h4>
                  <p>GDPR-aware, locally grounded, no outsourcing</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          className="how-we-build"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <h3>How We Build</h3>
          <p>
            Many digital projects fail not <span className="gradient-text">because of bad code</span> but because they're 
            overengineered from day one.
          </p>
          <p>
            At Leviathan Coding Systems, we design solutions to match your current needs while 
            leaving room to grow. That means:
          </p>
          
          <div className="build-principles">
            <div className="build-item">
              <div className="build-icon">🎯</div>
              <div className="build-text">
                <h4>Starting lean and focused</h4>
                <p>Build what you need now, not what you might need in three years</p>
              </div>
            </div>
            <div className="build-item">
              <div className="build-icon">📐</div>
              <div className="build-text">
                <h4>Designing with structure</h4>
                <p>Clean architecture that makes future updates straightforward</p>
              </div>
            </div>
            <div className="build-item">
              <div className="build-icon">🔧</div>
              <div className="build-text">
                <h4>Keeping it maintainable</h4>
                <p>Readable code, clear documentation, no vendor lock-in</p>
              </div>
            </div>
            <div className="build-item">
              <div className="build-icon">🚀</div>
              <div className="build-text">
                <h4>Delivering real value</h4>
                <p>Every feature serves a purpose and solves an actual problem</p>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          className="who-we-work-with"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <h3>Who We Typically Work With</h3>
          <p>
            We support businesses across Cheshire, Liverpool, Manchester and the wider North West 
            who are looking to modernise their digital presence and implement structured systems that scale.
          </p>
          
          <div className="client-types">
            <div className="client-item">
              <div className="client-icon">🏗️</div>
              <h4>Trades & Service Providers</h4>
              <p>Establishing credibility and generating quality enquiries</p>
            </div>
            <div className="client-item">
              <div className="client-icon">🏥</div>
              <h4>Clinics & Consultants</h4>
              <p>Introducing online bookings and streamlining client management</p>
            </div>
            <div className="client-item">
              <div className="client-icon">📈</div>
              <h4>Growing Small Businesses</h4>
              <p>Streamlining enquiries and scaling operations</p>
            </div>
            <div className="client-item">
              <div className="client-icon">🚀</div>
              <h4>Founders & Visionaries</h4>
              <p>Building structured digital infrastructure from the ground up</p>
            </div>
          </div>

          <p className="closing-statement">
            If you're looking for clarity, long-term reliability, and practical growth support <span className="gradient-text">we're likely a strong fit.</span>
          </p>
        </motion.div>

        <motion.div
          className="dive-deeper-banner"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <div className="dive-deeper-copy">
            <div className="section-badge dive-deeper-badge">
              <span className="badge-icon">↘</span>
              <span className="dive-deeper-badge-text gradient-text">Dive Deeper</span>
            </div>
            <h3>Explore the thinking behind Leviathan in more detail</h3>
            <p>
              For visitors who want a closer look at the person behind the studio, the local focus,
              and the commercial thinking behind our work, these pages go further than the homepage.
            </p>
          </div>

          <div className="dive-deeper-links">
            {deepDivePages.map((page) => (
              <Link key={page.slug} to={`/${page.slug}`} className="deep-dive-card">
                <span className="deep-dive-card-index">{page.cardIndex}</span>
                <h4>{page.cardTitle}</h4>
                <p>{page.cardSummary}</p>
                <span className="deep-dive-card-cta">Read more</span>
              </Link>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default About;
